/**
 * SignUpScreen — création de compte email + mot de passe.
 *
 * v1.12.117 (Phase 1) : pas d'email verification (auto-confirm côté Lambda).
 * Phase 2 (à venir) : SES no-reply@mytenue.com → envoi d'un lien de validation
 *   email obligatoire avant connexion + reset password fonctionnel.
 *
 * Flow :
 *  1. User remplit firstName, lastName, email, password, confirmPassword
 *  2. Validation locale (formats + match passwords)
 *  3. Appel AuthContext.signUpWithEmail → Lambda /auth/email-signup
 *  4. Sur succès : tokens reçus, user authentifié, navigate Main
 */

import React, { useState } from 'react';
import {
  SafeAreaView, View, Text, TextInput, TouchableOpacity, Image,
  StyleSheet, Alert, ActivityIndicator, ScrollView,
  KeyboardAvoidingView, Platform,
} from 'react-native';
import { useNavigation, StackActions } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { showAppAlert } from '../components/AppDialog';
import { rootReplace } from '../utils/rootNavigation';
import { colors, spacing, radius, shadow } from '../theme';
import { useI18n } from '../i18n';
import { openLegalUrl } from '../utils/openLegalUrl';

function isEmailValid(e) {
  return typeof e === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
}
function passwordIssues(p, t) {
  if (typeof p !== 'string' || p.length < 8) return t('err_password_min_length');
  if (!/[a-z]/.test(p)) return t('err_password_lowercase');
  if (!/[A-Z]/.test(p)) return t('err_password_uppercase');
  if (!/[0-9]/.test(p)) return t('err_password_digit');
  return null;
}

export default function SignUpScreen() {
  const navigation = useNavigation();
  const { theme } = useTheme();
  const { signUpWithEmail } = useAuth();
  const { t, lang } = useI18n();

  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [acceptCgu, setAcceptCgu] = useState(false);
  const [busy,      setBusy]      = useState(false);
  const [showPwd,   setShowPwd]   = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // v1.12.118 — erreurs inline (champs rouges)
  const [errors,    setErrors]    = useState({}); // { firstName?, lastName?, email?, password?, confirm?, cgu? }

  // Reset errors quand l'user retape dans un champs
  const updateField = (key, val, setter) => {
    setter(val);
    if (errors[key]) setErrors(e => ({ ...e, [key]: undefined }));
  };

  const onSubmit = async () => {
    if (busy) return;
    const fn = firstName.trim();
    const ln = lastName.trim();
    const em = email.trim().toLowerCase();
    const errs = {};
    if (!fn) errs.firstName = t('err_first_name_required');
    if (!ln) errs.lastName  = t('err_last_name_required');
    if (!isEmailValid(em)) errs.email = !em ? t('err_email_required') : t('err_email_invalid');
    const pwIssue = passwordIssues(password, t);
    if (pwIssue) errs.password = pwIssue;
    if (password && confirm && password !== confirm) errs.confirm = t('err_passwords_mismatch');
    if (!confirm && password) errs.confirm = t('err_confirm_password');
    if (!acceptCgu) errs.cgu = t('err_accept_cgu');

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});

    setBusy(true);
    const result = await signUpWithEmail({
      email: em, password, firstName: fn, lastName: ln,
    });
    setBusy(false);

    if (!result?.success) {
      // Cas spécial : email déjà inscrit → proposer login OU mdp oublié
      const errMsg = (result?.error || '').toLowerCase();
      const isAlreadyRegistered =
        errMsg.includes('compte existe déj') ||
        errMsg.includes('compte deja') ||
        errMsg.includes('already exists') ||
        errMsg.includes('already used') ||
        errMsg.includes('compte déjà') ||
        errMsg.includes('email est déjà');

      if (isAlreadyRegistered) {
        const goLogin  = () => setTimeout(() => rootReplace('Login',         { prefillEmail: em }), 100);
        const goForgot = () => setTimeout(() => rootReplace('ForgotPassword',{ prefillEmail: em }), 100);

        showAppAlert(
          t('account_exists_title'),
          t('account_exists_msg').replace('{email}', em),
          [
            { text: t('cancel'),                style: 'cancel'   },
            { text: t('forgot_password_title'), style: 'default', onPress: goForgot },
            { text: t('sign_in'),               style: 'default', onPress: goLogin  },
          ],
        );
        return;
      }

      Alert.alert(t('signup_failed_title'), result?.error || t('signup_failed_msg'));
      return;
    }

    // Phase 2 : signup n'authentifie PAS directement. L'user doit confirmer
    // son email avec le code reçu, puis se connecter manuellement avec ses
    // identifiants. UX explicite via une alerte avant la navigation.
    if (result.needsConfirmation) {
      const finalEmail = result.email || em;
      const successMessage = t('signup_email_sent_msg').replace('{email}', finalEmail);

      // rootReplace utilise le navigationRef global → bypass modal scope
      const goToConfirm = () => {
        setTimeout(() => rootReplace('ConfirmEmail', {
          email: finalEmail,
          firstName: result.firstName || fn,
          lastName:  result.lastName  || ln,
        }), 100);
      };

      // AppDialog brandé (cross-platform : modal MyTenue sur web, Alert
      // natif sur iOS/Android). showAppAlert renvoie l'index du bouton cliqué.
      showAppAlert(
        t('signup_email_sent_title'),
        successMessage,
        [{ text: t('enter_code_cta'), style: 'default' }],
      ).then(() => goToConfirm());
      return;
    }
    // Mode démo (IS_AWS_CONFIGURED=false) : connecté direct, ferme le modal.
    try { navigation.canGoBack() && navigation.goBack(); } catch {}
  };

  const pwIssueLive = password ? passwordIssues(password, t) : null;

  return (
    <SafeAreaView style={[s.safe, { backgroundColor: theme.bg }]}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex: 1 }}
      >
        <ScrollView
          contentContainerStyle={s.scroll}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={s.header}>
            {/* v1.12.929 — Logo MyTenue gold (hangers + tagline) — cohérent avec LoginScreen + emails */}
            <Image
              source={require('../../assets/logo-login-gold.png')}
              style={s.logoGold}
              resizeMode="contain"
            />
            <Text style={[s.title, { color: theme.text }]}>{t('signup_title')}</Text>
            <Text style={[s.subtitle, { color: theme.textFaint }]}>
              {t('signup_subtitle')}
            </Text>
          </View>

          {/* Form */}
          <View style={s.form}>
            <View style={s.row2}>
              <View style={{ flex: 1 }}>
                <Text style={[s.label, { color: theme.textFaint }]}>{t('first_name')}</Text>
                <TextInput
                  value={firstName}
                  onChangeText={(v) => updateField('firstName', v, setFirstName)}
                  placeholder={t('signup_first_name_placeholder')}
                  autoCapitalize="words"
                  textContentType="givenName"
                  style={[s.input, { borderColor: theme.bgBorder, color: theme.text, backgroundColor: theme.bgSurface }, errors.firstName && s.inputError]}
                  placeholderTextColor={theme.textFaint}
                />
                {errors.firstName && <Text style={s.errorText}>{errors.firstName}</Text>}
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[s.label, { color: theme.textFaint }]}>{t('last_name')}</Text>
                <TextInput
                  value={lastName}
                  onChangeText={(v) => updateField('lastName', v, setLastName)}
                  placeholder={t('signup_last_name_placeholder')}
                  autoCapitalize="words"
                  textContentType="familyName"
                  style={[s.input, { borderColor: theme.bgBorder, color: theme.text, backgroundColor: theme.bgSurface }, errors.lastName && s.inputError]}
                  placeholderTextColor={theme.textFaint}
                />
                {errors.lastName && <Text style={s.errorText}>{errors.lastName}</Text>}
              </View>
            </View>

            <Text style={[s.label, { color: theme.textFaint }]}>{t('email_label')}</Text>
            <TextInput
              value={email}
              onChangeText={(v) => updateField('email', v, setEmail)}
              placeholder={t('email_placeholder')}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              textContentType="emailAddress"
              style={[s.input, { borderColor: theme.bgBorder, color: theme.text, backgroundColor: theme.bgSurface }, errors.email && s.inputError]}
              placeholderTextColor={theme.textFaint}
            />
            {errors.email && <Text style={s.errorText}>{errors.email}</Text>}

            <Text style={[s.label, { color: theme.textFaint }]}>{t('password')}</Text>
            <View style={{ position: 'relative' }}>
              <TextInput
                value={password}
                onChangeText={(v) => updateField('password', v, setPassword)}
                placeholder={t('password_placeholder')}
                secureTextEntry={!showPwd}
                autoCapitalize="none"
                autoCorrect={false}
                textContentType="newPassword"
                style={[s.input, { paddingRight: 60, borderColor: theme.bgBorder, color: theme.text, backgroundColor: theme.bgSurface }, errors.password && s.inputError]}
                placeholderTextColor={theme.textFaint}
              />
              <TouchableOpacity
                onPress={() => setShowPwd(v => !v)}
                style={s.eyeBtn}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Text style={[s.eyeBtnText, { color: theme.textFaint }]}>{showPwd ? t('hide_password') : t('show_password')}</Text>
              </TouchableOpacity>
            </View>
            {(errors.password || pwIssueLive) && (
              <Text style={s.errorText}>{errors.password || pwIssueLive}</Text>
            )}

            <Text style={[s.label, { color: theme.textFaint }]}>{t('confirm_password')}</Text>
            <View style={{ position: 'relative' }}>
              <TextInput
                value={confirm}
                onChangeText={(v) => updateField('confirm', v, setConfirm)}
                placeholder={t('password_retype_placeholder')}
                secureTextEntry={!showConfirm}
                autoCapitalize="none"
                autoCorrect={false}
                textContentType="newPassword"
                style={[s.input, { paddingRight: 60, borderColor: theme.bgBorder, color: theme.text, backgroundColor: theme.bgSurface }, errors.confirm && s.inputError]}
                placeholderTextColor={theme.textFaint}
              />
              <TouchableOpacity
                onPress={() => setShowConfirm(v => !v)}
                style={s.eyeBtn}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Text style={[s.eyeBtnText, { color: theme.textFaint }]}>{showConfirm ? t('hide_password') : t('show_password')}</Text>
              </TouchableOpacity>
            </View>
            {errors.confirm && <Text style={s.errorText}>{errors.confirm}</Text>}

            {/* CGU */}
            <TouchableOpacity
              onPress={() => { setAcceptCgu(v => !v); if (errors.cgu) setErrors(e => ({ ...e, cgu: undefined })); }}
              activeOpacity={0.75}
              style={s.cguRow}
            >
              <View style={[s.checkbox, acceptCgu && s.checkboxChecked, { borderColor: errors.cgu ? '#C62828' : theme.bgBorder }]}>
                {acceptCgu && <Text style={s.checkboxMark}>✓</Text>}
              </View>
              <Text style={[s.cguText, { color: errors.cgu ? '#C62828' : theme.textFaint }]}>
                {t('signup_legal_intro')} <Text style={{ textDecorationLine: 'underline', color: theme.text }} onPress={() => openLegalUrl('terms', lang)}>{t('signup_legal_terms_link')}</Text>
                {' '}{t('signup_legal_and')}{' '}
                <Text style={{ textDecorationLine: 'underline', color: theme.text }} onPress={() => openLegalUrl('privacy', lang)}>{t('signup_legal_privacy_link')}</Text>.
              </Text>
            </TouchableOpacity>

            {/* Submit */}
            <TouchableOpacity
              onPress={onSubmit}
              disabled={busy}
              activeOpacity={0.85}
              style={[s.submitBtn, busy && { opacity: 0.6 }]}
            >
              {busy
                ? <ActivityIndicator color="#fff" size="small" />
                : <Text style={s.submitText}>{t('signup_submit_cta')}</Text>}
            </TouchableOpacity>

            <TouchableOpacity onPress={() => rootReplace('Login', email ? { prefillEmail: email.trim().toLowerCase() } : undefined)}>
              <Text style={[s.altLink, { color: theme.textFaint }]}>
                {t('already_have_account')} <Text style={{ color: '#9B7B5A', fontWeight: '600' }}>{t('sign_in_cta')}</Text>
              </Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe: { flex: 1 },
  scroll: { padding: spacing.xl, gap: 18, paddingBottom: 40 },
  header: { alignItems: 'center', gap: 8, marginTop: 12, marginBottom: 4 },
  // v1.12.929 — Logo gold (280×108 comme LoginScreen)
  logoGold: { width: 280, height: 108, marginBottom: 4, resizeMode: 'contain' },
  title: { fontSize: 22, fontWeight: '700', letterSpacing: 0.3, marginTop: 8 },
  subtitle: { fontSize: 13, textAlign: 'center', maxWidth: 320 },

  form: { gap: 6 },
  row2: { flexDirection: 'row', gap: 10, marginBottom: 4 },
  label: { fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', marginTop: 6, marginBottom: 4, fontWeight: '600' },
  input: {
    height: 46,
    borderWidth: 1,
    borderRadius: radius.md,
    paddingHorizontal: 14,
    fontSize: 14,
  },
  eyeBtn: {
    position: 'absolute', right: 12, top: 0, bottom: 0,
    justifyContent: 'center',
  },
  eyeBtnText: { fontSize: 11, fontWeight: '600', letterSpacing: 0.4 },
  pwHint: { fontSize: 11, marginTop: 4, marginLeft: 2 },
  // v1.12.118 — erreurs inline (champs rouges + message)
  inputError: {
    borderColor: '#C62828',
    borderWidth: 1.5,
    backgroundColor: '#FFF5F5',
  },
  errorText: {
    fontSize: 11,
    color: '#C62828',
    marginTop: 4,
    marginLeft: 2,
    fontWeight: '500',
  },

  cguRow: { flexDirection: 'row', gap: 10, alignItems: 'flex-start', marginTop: 14, marginBottom: 4 },
  checkbox: {
    width: 20, height: 20, borderRadius: 4,
    borderWidth: 1.5,
    alignItems: 'center', justifyContent: 'center',
    marginTop: 2,
  },
  checkboxChecked: { backgroundColor: '#9B7B5A', borderColor: '#9B7B5A' },
  checkboxMark: { color: '#fff', fontSize: 12, fontWeight: '700' },
  cguText: { flex: 1, fontSize: 12, lineHeight: 17 },

  submitBtn: {
    height: 50,
    borderRadius: radius.md,
    backgroundColor: '#9B7B5A',
    alignItems: 'center', justifyContent: 'center',
    marginTop: 14,
    ...shadow.sm,
  },
  submitText: { color: '#fff', fontSize: 14, fontWeight: '700', letterSpacing: 1 },

  altLink: { textAlign: 'center', fontSize: 13, marginTop: 14 },
});
